import { useState } from 'react';
import { Pressable, View } from 'react-native';

import { Box, HStack, Text, Toggle, VStack } from '@squire/ui';

import { ChevronDownIcon } from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { TrackerDetails } from './TrackerDetails';
import { getCookieConsentCategories } from './cookieConsentCategories';
import { styles } from './styles';
import type { CookieConsentDetailsProps, Tracker } from './types';

const CookieConsentDetails = ({
  selectedCategories,
  handleCategoryToggle,
}: CookieConsentDetailsProps) => {
  const { t } = useScreenTranslations('CookieConsentDetails');

  const categories = getCookieConsentCategories(t);

  const [selectedSubsection, setSelectedSubsection] = useState<Tracker | null>(
    null,
  );

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['necessary']),
  );

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }

      return newSet;
    });
  };

  const renderTrackerCard = (tracker: Tracker) => (
    <Pressable
      key={tracker.id}
      style={styles.trackerCard}
      onPress={() => setSelectedSubsection(tracker)}
    >
      <HStack style={styles.trackerHeader}>
        <Box style={styles.trackerLogo}>
          {<tracker.Icon color="iconPrimary" size={12} />}
        </Box>
        <VStack style={styles.trackerInfo}>
          <Text
            variant="s"
            color="textPrimaryInverse"
            style={styles.trackerName}
          >
            {tracker.name}
          </Text>
          <Text
            variant="sStrong"
            color="textTertiary"
            style={styles.trackerCount}
          >
            {t('trackerCount', { count: tracker.items.length })}
          </Text>
        </VStack>
      </HStack>

      <Text
        variant="s"
        color="textPrimaryInverse"
        style={styles.trackerDescription}
      >
        {tracker.id === 'google'
          ? t(`trackers.google.description`)
          : t(`trackers.cloudflare.description`)}
      </Text>
    </Pressable>
  );

  const renderCategory = (category: (typeof categories)[0]) => {
    const isExpanded = expandedCategories.has(category.id);
    const hasTrackers = category.trackers && category.trackers.length > 0;

    return (
      <VStack key={category.id} style={styles.categorySection}>
        <HStack style={styles.categoryHeader}>
          <HStack style={styles.categoryHeaderContent}>
            {hasTrackers && (
              <Pressable
                onPress={() => toggleCategory(category.id)}
                style={styles.categoryHeaderButton}
                testID={`chevron-${category.id}`}
              >
                <View
                  style={
                    isExpanded ? styles.arrowExpanded : styles.arrowCollapsed
                  }
                >
                  <ChevronDownIcon size={16} color="textPrimaryInverse" />
                </View>
              </Pressable>
            )}
            <Text
              variant="m"
              color="textPrimaryInverse"
              style={styles.categoryTitle}
            >
              {category.title}
            </Text>
          </HStack>
          <Toggle
            value={selectedCategories[category.id]}
            onValueChange={(enabled) => {
              handleCategoryToggle(category.id, enabled);
            }}
            pointerEvents={category.required ? 'none' : 'auto'}
            accessibilityLabel={t('toggleCategory', {
              category: category.title,
            })}
          />
        </HStack>
        <Text
          variant="s"
          color="textSecondaryInverse"
          style={styles.categoryDescription}
        >
          {category.description}
        </Text>

        {isExpanded && hasTrackers && (
          <VStack style={styles.trackersContainer}>
            {category.trackers?.map(renderTrackerCard)}
          </VStack>
        )}
      </VStack>
    );
  };

  return (
    <VStack style={[styles.container]}>
      {selectedSubsection ? (
        <TrackerDetails
          tracker={selectedSubsection}
          onGoBack={() => setSelectedSubsection(null)}
        />
      ) : (
        categories.map(renderCategory)
      )}
    </VStack>
  );
};

CookieConsentDetails.displayName = 'CookieConsentDetails';

export { CookieConsentDetails };
